import { Injectable } from '@angular/core';
import { Coordinate } from '@core/api';
import { environment } from '@core/environments/environment';
import * as mapsRestService from 'azure-maps-rest';
import {
  Aborter,
  MapsURL,
  SearchURL,
  SubscriptionKeyCredential,
} from 'azure-maps-rest';
import { from, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface LocationResult {
  name: string;
  coordinates: Coordinate;
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly pipeline: mapsRestService.Pipeline;

  constructor() {
    const subscriptionKeyCredential = new SubscriptionKeyCredential(
      environment.mapAuthOption!.subscriptionKey!
    );

    this.pipeline = MapsURL.newPipeline(subscriptionKeyCredential, {
      retryOptions: { maxTries: 4 },
    });
  }

  /**
   * Retrieves the coordinates (latitude and longitude) for a given query string.
   * @param query The address or location query string.
   * @returns A promise that resolves to a tuple containing the latitude and longitude.
   */
  public async getCoordinatesFromQuery(
    query: string
  ): Promise<LocationResult[]> {
    const searchURL = new SearchURL(this.pipeline);
    const searchOptions: mapsRestService.SearchAddressOptions = {
      countrySet: ['DE'],
      language: 'de',
      typeahead: true,
      limit: 100,
      idxSet: ['Geo'],
    };
    const response = await searchURL.searchAddress(
      Aborter.timeout(10000),
      query,
      searchOptions
    );
    if (response?.results && response.results.length > 0) {
      const geographyResults = response.results.filter(
        (result: any) =>
          result.position &&
          result.entityType &&
          (result.entityType.toLowerCase().includes('municipality') ||
            result.entityType.toLowerCase().includes('postalcodearea'))
      );

      // Sammle alle Namen und zähle Duplikate
      const nameCount = new Map<string, number>();
      geographyResults.forEach((result: any) => {
        const name = result.address?.freeformAddress ?? 'Unbekannter Ort';
        nameCount.set(name, (nameCount.get(name) || 0) + 1);
      });

      const locations = geographyResults.map((result): LocationResult => {
        const baseName = result.address?.freeformAddress ?? 'Unbekannter Ort';
        const isDuplicate = nameCount.get(baseName)! > 1;
        const countrySubdivision = result.address?.countrySubdivision;

        const name =
          isDuplicate && countrySubdivision
            ? `${baseName} (${countrySubdivision})`
            : baseName;

        return {
          name,
          coordinates: {
            latitude: result.position!.lat,
            longitude: result.position!.lon,
          },
        };
      });
      return locations;
    }
    return [];
  }

  /**
   * Retrieves the city for a given set of coordinates (latitude and longitude).
   * @param lat The latitude.
   * @param lon The longitude.
   * @returns A promise that resolves to the city of the given coordinates.
   */
  public async getCityFromCoordinates(
    lat: number,
    lon: number
  ): Promise<string> {
    const searchURL = new SearchURL(this.pipeline);
    const response = await searchURL.searchAddressReverse(
      Aborter.timeout(10000),
      [lon, lat],
      { language: 'de' }
    );
    if (
      (response?.summary?.numResults ?? 0) > 0 &&
      response?.addresses?.[0]?.address?.municipality
    ) {
      return response.addresses[0].address.municipality;
    }
    return '';
  }

  getSuggestions(searchTerm: string): Observable<LocationResult[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return of([]);
    }

    return from(this.getCoordinatesFromQuery(searchTerm)).pipe(
      catchError(error => {
        console.error('Error getting suggestions:', error);
        return of([]);
      })
    );
  }
}
